import { getIntl } from '../../../../../../utils/intl';
import { usePromptButton } from '../../../../../shared/PromptButton';
import { Action } from '../../../../../v2BaseComponents/ActionButtonLayout/types';
import { ContentStack } from '../../../../../v2BaseComponents/Stack';
import useMedicalProfileFiles from '../../../../../../hooks/api/useMedicalProfileFiles';
import FolderSelection from '../../../../../shared/FolderSelection';

const intl = getIntl('MoveFolderButton', {
    promptHeader: 'Move {folderName}',
    add: 'Add',
    folderError: 'Folder cannot be moved into itself',
    root: 'Root',
});

export type UseMoveFolderActionArgs = {
    folderId: string;
    medicalProfileId: string;
    onSetFolderId: (folderId: string) => void;
    folderOrganizationId: string | null;
};

export type UseMoveFolderActionReturnValue = {
    action: Action | null;
    Prompt: React.ReactNode;
};

export const useMoveFolderAction = (args: UseMoveFolderActionArgs): UseMoveFolderActionReturnValue => {
    const { onMoveFolder, isMovingFolder, folderMetadata } = useMedicalProfileFiles({
        medicalProfileId: args.medicalProfileId,
        folderId: args.folderId,
        onCreateFolderSuccess: ({ medicalProfileFileFolderId }) => {
            args.onSetFolderId(medicalProfileFileFolderId);
        },
    });
    const folderName =
        folderMetadata?.targetFolder.isRoot === true
            ? intl('root')
            : folderMetadata?.targetFolder.folder.folderName || '';

    const promptButton = usePromptButton({
        promptTitle: intl('promptHeader', { folderName }),
        renderPromptContents: (onClose) =>
            folderMetadata?.targetFolder.isRoot === false ? (
                <ContentStack>
                    <FolderSelection
                        startFolderId={'root'}
                        onSelectFolder={(newParentFolderId) => {
                            onMoveFolder({
                                folderId: args.folderId,
                                newParentFolderId: newParentFolderId,
                            });
                            onClose();
                        }}
                        onClose={onClose}
                        medicalProfileId={args.medicalProfileId}
                        getIsDisabled={(folderId) => {
                            const isDisabled = folderId === args.folderId;
                            return isDisabled;
                        }}
                    />
                </ContentStack>
            ) : null,
    });

    if (!folderMetadata || folderMetadata.targetFolder.isRoot === true) return { action: null, Prompt: null };

    return {
        action: {
            title: intl('promptHeader', { folderName }),
            icon: 'Move',
            onPress: promptButton.onPress,
            show: !args.folderOrganizationId,
            isDisabled: isMovingFolder,
            testID: 'move-folder-button',
        },
        Prompt: promptButton.Prompt,
    };
};
